import { createFileRoute } from "@tanstack/react-router"
import { useRef, useState } from "react"
import { LoadingOverlay, Group, ActionIcon, Tooltip } from "@mantine/core"
import { DepartmentDutyRosterSpreadsheet } from "../../-components/DutyScheduling/duty-roster-spreadsheet"
import { SuspenseWithErrorBoundary } from "../../../../-components/suspense"
import { useValidatedState } from "@mantine/hooks"
import { useEffect } from "react"
import { Page } from "../../../../-components/Page"
import { DatePeriodSelect } from "../orchestra/-components/period-select"
import { keepPreviousData } from "@tanstack/react-query"
import { useGridStoreInitializer } from "../../-components/DutyScheduling/state"
import { DutySchedulingContainer } from "../-components/-DutySchedulingContainer"
import { useDepartmentDutyRosterGet, useGetFormulaResults } from "@/orval/gen"
import { collectContractIds } from "../-components/collect-contracts"
import { dateToDateOnly } from "../../../../utils/date-to-date-only"
import { DateOnly } from "../../../../@client/System"
import { DepartmentWorkareaTreeSelect } from "@components/tree-select/DepartmentWorkareaTreeSelect"
import { QuickAddShiftPanel } from "../shifts-and-casts/-components/QuickAddShiftPanel"
import { useQuickAddPanelState } from "../-components/useQuickAddPanelState"
import { QuickQueueWidget } from "../-components/QuickQueueWidget"
import { QuickQueueStats } from "../../-components/DutyScheduling/duty-roster-spreadsheet"
import { useUserLocalStorage } from "../../-components/use-user-local-storage"
import { useTranslation } from "react-i18next"
import {
  IconLayoutSidebarRightCollapse,
  IconLayoutSidebarRightExpand,
} from "@tabler/icons-react"

export const Route = createFileRoute("/app/duties/absences/")({
  component: RouteComponent,
})

const initialRange: [DateOnly | null, DateOnly | null] = [null, null]
const QUICK_ADD_PANEL_VISIBLE_KEY =
  "duty-scheduling/absences/quick-add-panel-visible"
const QUICK_ADD_SELECTED_SHIFT_KEY =
  "duty-scheduling/absences/selected-quick-add-shift"

function arraysAreEqual<T>(a: T[], b: T[]) {
  if (a.length !== b.length) return false
  const aSorted = [...a].sort()
  const bSorted = [...b].sort()
  return aSorted.every((v, i) => v === bSorted[i])
}

function RouteComponent() {
  const [{ value: range, lastValidValue: lastValidRange }, setRange] =
    useValidatedState(initialRange, (v) => Boolean(v[0] && v[1]))
  const [
    storedSelectedWorkareasOrDepartments,
    setStoredSelectedWorkareasOrDepartments,
  ] = useUserLocalStorage<string[]>({
    key: "duty-scheduling/absences/selected-workareas-or-departments",
    defaultValue: [],
  })
  const [selectedWorkareasOrDepartments, setSelectedWorkareasOrDepartments] =
    useState<string[]>(storedSelectedWorkareasOrDepartments)
  const {
    quickAddPanelVisible,
    setQuickAddPanelVisible,
    selectedQuickAddShift,
    setSelectedQuickAddShift,
  } = useQuickAddPanelState({
    panelVisibleStorageKey: QUICK_ADD_PANEL_VISIBLE_KEY,
    selectedShiftStorageKey: QUICK_ADD_SELECTED_SHIFT_KEY,
  })

  useEffect(() => {
    setStoredSelectedWorkareasOrDepartments(selectedWorkareasOrDepartments)
  }, [selectedWorkareasOrDepartments, setStoredSelectedWorkareasOrDepartments])

  const ref = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (ref.current) {
      if (range[0] === null && range[1] === null) {
        const cellWidth = (1210 - 128) / 48
        const cells = Math.max(7, (ref.current.offsetWidth - 130) / cellWidth)
        const start = new Date()
        const end = new Date()
        end.setDate(new Date().getDate() + cells)
        setRange([dateToDateOnly(start), dateToDateOnly(end)])
      }
    }
  }, [])
  const { t } = useTranslation("app_server")

  return (
    <Page ref={ref}>
      <Group>
        <DepartmentWorkareaTreeSelect
          w={200}
          maw={"100%"}
          label=""
          placeholder={t("Departments")}
          size="xs"
          value={selectedWorkareasOrDepartments}
          onChange={(v) =>
            setSelectedWorkareasOrDepartments((prev) =>
              arraysAreEqual(prev, v) ? prev : v
            )
          }
          params={{ withRoot: false, withModule: false }}
        />
        <DatePeriodSelect value={range} onChange={(v) => setRange(v)} />
        <Tooltip label={t("DutyScheduling_QuickAddMenu")}>
          <ActionIcon
            variant="default"
            size={30}
            aria-label={t("DutyScheduling_ToggleQuickAdd")}
            onClick={() => setQuickAddPanelVisible((v) => !v)}
          >
            {quickAddPanelVisible ? (
              <IconLayoutSidebarRightCollapse size={16} />
            ) : (
              <IconLayoutSidebarRightExpand size={16} />
            )}
          </ActionIcon>
        </Tooltip>
      </Group>

      <div
        style={{
          display: "flex",
          alignItems: "stretch",
          gap: 8,
          height: "calc(100vh - 180px)",
          minHeight: 480,
        }}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          <DutySchedulingContainer h="100%" mah="100%">
            {lastValidRange && lastValidRange[0] && lastValidRange[1] ? (
              <InnerComponent
                from={lastValidRange[0]}
                until={lastValidRange[1]}
                departmentTreeNodes={selectedWorkareasOrDepartments}
              />
            ) : null}
          </DutySchedulingContainer>
        </div>

        {quickAddPanelVisible ? (
          <div
            style={{
              flex: "0 0 280px",
              width: 280,
              minWidth: 280,
              height: "100%",
              minHeight: 0,
              marginTop: 10,
            }}
          >
            <QuickAddShiftPanel
              departmentTreeNodes={selectedWorkareasOrDepartments}
              selectedQuickAddShift={selectedQuickAddShift}
              setSelectedQuickAddShift={setSelectedQuickAddShift}
              shiftKinds={["absence"]}
            />
          </div>
        ) : null}
      </div>
    </Page>
  )
}

function InnerComponent({
  from,
  until,
  departmentTreeNodes,
}: {
  from: string
  until: string
  departmentTreeNodes: string[]
}) {
  const { data, refetch, isFetching, dataUpdatedAt } =
    useDepartmentDutyRosterGet(
      {
        from,
        until,
        departmentTreeNodes,
        splitShifts: false,
      },
      { query: { placeholderData: keepPreviousData } }
    )
  const [isQuickQueueLoading, setIsQuickQueueLoading] = useState(false)
  const [quickQueueStats, setQuickQueueStats] = useState<QuickQueueStats>({
    visible: false,
    pending: 0,
    total: 0,
    completed: 0,
    percent: 0,
    running: false,
    cancelRequested: false,
  })
  const [quickQueueCancel, setQuickQueueCancel] = useState<(() => void) | null>(
    null
  )
  const contractIds = collectContractIds(data?.rowHeaders || [])
  const departmentId =
    departmentTreeNodes
      .filter((v) => v.startsWith("DepartmentId"))
      .map((v) => parseInt(v.replace("DepartmentId", "")))[0] || undefined
  const { data: formulaResults, dataUpdatedAt: formulaDataUpdatedAt } =
    useGetFormulaResults(
      {
        from,
        until,
        departmentId,
        contractIds: contractIds,
        formulaIds: [],
      },
      {
        query: {
          placeholderData: keepPreviousData,
          enabled: Boolean(departmentId),
        },
      }
    )

  const { storeInitialized, updateFormulaResults } = useGridStoreInitializer({
    data: data,
    compact: true,
    updatedAt: dataUpdatedAt,
  })
  const formulaResultValues = formulaResults?.values

  useEffect(() => {
    if (formulaResultValues) {
      updateFormulaResults(formulaResultValues)
    }
  }, [formulaDataUpdatedAt])

  return (
    <div style={{ position: "relative", minHeight: 200, minWidth: 200 }}>
      {data && storeInitialized ? (
        <SuspenseWithErrorBoundary>
          <DepartmentDutyRosterSpreadsheet
            departmentTreeNodes={departmentTreeNodes}
            focus="absence-planning"
            onRefetch={() => refetch()}
            onQueueLoadingChange={setIsQuickQueueLoading}
            onQueueStatsChange={setQuickQueueStats}
            onQueueCancelRegister={(cancel) => {
              setQuickQueueCancel(() => cancel)
            }}
            data={data}
          />
        </SuspenseWithErrorBoundary>
      ) : null}

      <QuickQueueWidget stats={quickQueueStats} onCancel={quickQueueCancel} />

      <LoadingOverlay
        visible={isFetching || isQuickQueueLoading}
        zIndex={15}
        overlayProps={{ radius: "sm", blur: 0 }}
      />
    </div>
  )
}
